import {
    MdOutlineAddCircleOutline,
    MdOutlineAttachFile,
    MdOutlineDeleteOutline,
    MdOutlineDescription,
    MdOutlineEdit,
    MdOutlineHistory,
    MdOutlinePerson,
    MdOutlineSwapHoriz,
    MdOutlineHandshake,
} from 'react-icons/md'

import {
    HistoryDiff,
    HistoryField,
    HistoryFields,
    HistoryFile,
    HistoryMuted,
    HistoryQuote,
} from '@/Ship/Components/History'
import { formatDateUk } from '@/Ship/Helpers/fieldViews'

import type { CrmHistoryChange, CrmHistoryEventMap, CrmHistoryItem, CrmHistoryKind } from './CrmHistoryEvent'
import type { HistoryEntry } from '@/Ship/Components/History'
import type { ReactNode } from 'react'

const ICONS: Record<CrmHistoryKind, ReactNode> = {
    created: <MdOutlineAddCircleOutline />,
    updated: <MdOutlineEdit />,
    removed: <MdOutlineDeleteOutline />,
    status: <MdOutlineSwapHoriz />,
    contact: <MdOutlinePerson />,
    contract: <MdOutlineHandshake />,
    document: <MdOutlineDescription />,
    file: <MdOutlineAttachFile />,
}

/** Підпис дочірнього запису в подробицях події. */
const CHILD_LABELS: Record<'contact' | 'contract' | 'document', string> = {
    contact: 'Контакт',
    contract: 'Договір',
    document: 'Документ',
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null && !Array.isArray(value)

const isBlank = (value: unknown): boolean =>
    value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0)

/**
 * Значення з `payload` — рядком для людини.
 *
 * Бек пише значення вже розв'язаними (назви, ПІБ, коди валют, `SPEC-008`
 * §5.5), тож тут лише склеюємо переліки й дістаємо назву з об'єкта, якщо
 * прийшов він.
 */
export const describeHistoryValue = (value: unknown): string => {
    const one = (item: unknown): string => {
        if (typeof item === 'string') return item
        if (typeof item === 'number' || typeof item === 'boolean') return String(item)

        if (isRecord(item)) {
            const title = item['label'] ?? item['title'] ?? item['name'] ?? item['fullName']

            return typeof title === 'string' ? title : ''
        }

        return ''
    }

    const text = Array.isArray(value) ? value.map(one).filter(Boolean).join(', ') : one(value)

    return text !== '' ? text : '—'
}

/**
 * Одна зміна поля: «було → стало», а якщо одного боку немає — «додано» /
 * «знято». Довгі поля (коментар) бек пише без значень (`masked`) — тоді лише
 * позначка «змінено» (`SPEC-009` §4.9).
 */
const changeRow = (change: CrmHistoryChange, index: number): ReactNode => {
    const label = change.label ?? change.field ?? '—'
    const key = `${change.field ?? label}-${index}`
    const hasOld = !isBlank(change.old)
    const hasNew = !isBlank(change.new)

    // Довге поле (коментар): у журналі лише позначка «змінено», без тексту
    if (change.masked === true || (!hasOld && !hasNew)) {
        return <HistoryField key={key} label={label} action="changed" />
    }

    if (!hasOld) {
        return (
            <HistoryField key={key} label={label} action="added">
                {describeHistoryValue(change.new)}
            </HistoryField>
        )
    }

    if (!hasNew) {
        return (
            <HistoryField key={key} label={label} action="removed">
                {describeHistoryValue(change.old)}
            </HistoryField>
        )
    }

    return (
        <HistoryField key={key} label={label}>
            <HistoryDiff from={describeHistoryValue(change.old)} to={describeHistoryValue(change.new)} />
        </HistoryField>
    )
}

const changesOf = (payload: Record<string, unknown>): CrmHistoryChange[] =>
    (Array.isArray(payload['changes']) ? payload['changes'] : []).filter(isRecord) as CrmHistoryChange[]

/** Підпис стану: `{status, label}` або сам рядок. */
const stateLabel = (value: unknown): string =>
    isRecord(value) ? describeHistoryValue(value['label'] ?? value['title'] ?? value['status']) : describeHistoryValue(value)

/** Договір, створений підтвердженням верифікації: номер, дата, експедитор. */
const contractRow = (contract: unknown): ReactNode => {
    if (!isRecord(contract)) return null

    const number = typeof contract['number'] === 'string' ? contract['number'] : describeHistoryValue(contract['title'])
    const date = typeof contract['date'] === 'string' && contract['date'] !== '' ? ` від ${formatDateUk(contract['date'])}` : ''
    const forwarder = isBlank(contract['forwarder']) ? '' : `, ${describeHistoryValue(contract['forwarder'])}`

    return (
        <HistoryField key="contract" label="Договір">
            {`${number}${date}${forwarder}`}
        </HistoryField>
    )
}

/**
 * Підпис категорії файлу власника (`SPEC-010` §12): `payload.category` (або
 * `payload.file.category`) → підпис поля картки. Невідома категорія — як є,
 * щоб її було видно, а не мовчки зникла.
 */
const fileCategoryRow = (
    payload: Record<string, unknown>,
    file: Record<string, unknown>,
    categories: Readonly<Record<string, string>> | undefined
): ReactNode => {
    const category = payload['category'] ?? file['category']

    if (typeof category !== 'string' || category === '') return null

    return (
        <HistoryField key="category" label="Категорія">
            {categories?.[category] ?? category}
        </HistoryField>
    )
}

const details = (
    kind: CrmHistoryKind,
    payload: Record<string, unknown>,
    fileCategories?: Readonly<Record<string, string>>
): ReactNode => {
    switch (kind) {
        case 'created': {
            const created = isRecord(payload['new']) ? payload['new'] : {}

            return isBlank(created['title']) ? null : (
                <HistoryFields>
                    <HistoryField label="Назва">{describeHistoryValue(created['title'])}</HistoryField>
                </HistoryFields>
            )
        }

        case 'updated': {
            const changes = changesOf(payload)

            return changes.length > 0 ? <HistoryFields>{changes.map(changeRow)}</HistoryFields> : null
        }

        case 'status': {
            const rows: ReactNode[] = []

            if ('old' in payload || 'new' in payload) {
                rows.push(
                    <HistoryField key="status" label="Статус">
                        <HistoryDiff from={stateLabel(payload['old'])} to={stateLabel(payload['new'])} strike={false} />
                    </HistoryField>
                )
            }

            if ('contract' in payload) rows.push(contractRow(payload['contract']))

            const comment = payload['comment']

            if (typeof comment === 'string' && comment !== '') {
                rows.push(
                    <HistoryField key="comment" label="Причина">
                        <HistoryQuote>{comment}</HistoryQuote>
                    </HistoryField>
                )
            }

            return rows.length > 0 ? <HistoryFields>{rows}</HistoryFields> : null
        }

        case 'contact':
        case 'contract':
        case 'document': {
            const child = payload[kind]
            const rows: ReactNode[] = []

            if (isRecord(child)) {
                rows.push(
                    <HistoryField key="child" label={CHILD_LABELS[kind]}>
                        {describeHistoryValue(child['title'] ?? child['number'])}
                    </HistoryField>
                )

                if (!isBlank(child['documentType'])) {
                    rows.push(
                        <HistoryField key="type" label="Тип документа">
                            {describeHistoryValue(child['documentType'])}
                        </HistoryField>
                    )
                }
            }

            // Генерація з шаблону (`SPEC-012` §6.3): юрособа й печатка
            if (kind === 'document' && isRecord(payload['legalEntity'])) {
                rows.push(
                    <HistoryField key="legalEntity" label="Юридична особа">
                        {describeHistoryValue(payload['legalEntity'])}
                    </HistoryField>
                )
            }

            if (kind === 'document' && typeof payload['isStamp'] === 'boolean') {
                rows.push(
                    <HistoryField key="isStamp" label="З печаткою та підписом">
                        {payload['isStamp'] ? 'Так' : 'Ні'}
                    </HistoryField>
                )
            }

            rows.push(...changesOf(payload).map(changeRow))

            return rows.length > 0 ? <HistoryFields>{rows}</HistoryFields> : null
        }

        case 'file': {
            const file = isRecord(payload['file']) ? payload['file'] : {}
            const target = isRecord(payload['target']) ? payload['target'] : null

            return (
                <HistoryFields>
                    <HistoryField label="Файл">
                        <HistoryFile name={describeHistoryValue(file['name'] ?? file['originalName'])} />
                    </HistoryField>
                    {fileCategoryRow(payload, file, fileCategories)}
                    {target && !isBlank(target['title']) && target['type'] !== 'owner' ? (
                        <HistoryField label={target['type'] === 'contract' ? 'Договір' : 'Документ'}>
                            {describeHistoryValue(target['title'])}
                        </HistoryField>
                    ) : null}
                </HistoryFields>
            )
        }

        default:
            return null
    }
}

/**
 * Події історії власника CRM → рядки спільної стрічки `HistoryTimeline`.
 *
 * Невідомий код (бек додав подію, а фронт ще ні) не ховаємо: показуємо його
 * як є — так помітно, що мапу треба доповнити. Вартовий ловить це раніше.
 *
 * `fileCategories` — підписи категорій файлів власника (у перевізника — 4,
 * `SPEC-010` §12); у власника без категорій не передається.
 */
export const buildCrmHistoryEntries = (
    items: readonly CrmHistoryItem[],
    map: CrmHistoryEventMap,
    fileCategories?: Readonly<Record<string, string>>
): HistoryEntry[] =>
    items.map((item, index) => {
        const info = map[item.event]
        const payload = isRecord(item.payload) ? item.payload : {}

        return {
            key: item.uuid ?? `${item.event}-${item.occurredAt ?? ''}-${index}`,
            title: info?.label ?? item.event,
            icon: info ? ICONS[info.kind] : <MdOutlineHistory />,
            tone: info?.tone ?? 'neutral',
            actor: item.changedBy ?? null,
            occurredAt: item.occurredAt,
            details: info ? details(info.kind, payload, fileCategories) : <HistoryMuted>{item.event}</HistoryMuted>,
        }
    })
